import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { post, get } from '@/api';
import { jwtDecode } from 'jwt-decode';
import { storageKeys } from '@/constants';
import { setSentryUser, clearSentryUser } from '@/observability/sentry';
import {
  identifyAnalyticsUser,
  resetAnalyticsIdentity,
} from '@/observability/identity';
import { resetPaywallExposure } from '@/observability/paywall';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    accessToken: useStorage(storageKeys.accessToken, ''),
    refreshToken: useStorage(storageKeys.refreshToken, ''),
    isfetchUserLoading: false,
  }),
  getters: {
    userInitials: (state) => {
      if (!state.user) return '';
      const { full_name } = state.user;
      const [firstName, lastName] = full_name.split(' ');
      return (firstName?.charAt(0) || '') + (lastName?.charAt(0) || '');
    },
    isAuthenticated: (state) => {
      if (state.accessToken && state.refreshToken) {
        return jwtDecode(state.accessToken).exp > Date.now() / 1000;
      }
      return false;
    },
    handle: (state) => state.user?.handle || null,
    referralCode: (state) => state.user?.referral_code || null,
    handleIsAutoGenerated: (state) =>
      Boolean(state.user?.handle_is_auto_generated),
    isLeaderboardEligible: (state) => Boolean(state.user?.handle),
    // Subscription / trial getters
    trialDaysRemaining: (state) => state.user?.trial_days_remaining ?? 0,
    // Trial state comes from the server (an active trial row with no paid
    // row) - a paid user with a leftover trial row is NOT "on trial".
    isTrialActive: (state) => Boolean(state.user?.is_trial),
    isSubscribed: (state) => Boolean(state.user?.is_subscribed),
    hasBundle: (state) => Boolean(state.user?.has_bundle),
    trialEligible: (state) => Boolean(state.user?.trial_eligible),
    // Soonest paid sub ending within the renewal window (null when covered
    // by a queued renewal / longer bundle) - drives the renewal banner.
    expiringSubscription: (state) => state.user?.expiring_subscription ?? null,
    subscribedBoardUuids: (state) => state.user?.subscribed_board_uuids ?? [],
    derivedBoardUuids: (state) => state.user?.derived_board_uuids ?? [],
    subscribedSubjectIds: (state) => state.user?.subscribed_subject_ids ?? [],
    trialStartDate: (state) => state.user?.trial_start_date ?? null,
  },
  actions: {
    async loginUser(credentials) {
      const { data, status } = await post('/auth/student-login/', credentials);
      if (status === 200) {
        this.accessToken = data.access;
        this.refreshToken = data.refresh;
        const response = await this.fetchUser();
        return { data, status };
      } else {
        return { data, status };
      }
    },

    // Google OAuth: exchange the GIS popup auth code at the backend.
    // Two response shapes: {status:'authenticated', access, refresh} →
    // logged in; {status:'needs_profile', signup_token, prefill} → the
    // caller routes into the Complete Your Profile step.
    async loginWithGoogle(code) {
      const { data, status } = await post('/auth/google/', { code });
      if (status === 200 && data?.status === 'authenticated') {
        this.accessToken = data.access;
        this.refreshToken = data.refresh;
        await this.fetchUser();
      }
      return { data, status };
    },

    async completeGoogleRegistration(payload) {
      const { data, status } = await post(
        '/auth/google/complete-registration/',
        payload
      );
      if (status === 200 && data?.status === 'authenticated') {
        this.accessToken = data.access;
        this.refreshToken = data.refresh;
        await this.fetchUser();
      }
      return { data, status };
    },

    async refreshToken() {
      try {
        const { data } = await post('/auth/refresh/', {
          refresh: this.refreshToken,
        });
        this.accessToken = data.access;
        return true;
      } catch (error) {
        return false;
      }
    },

    async fetchUser({ force = false } = {}) {
      if (this.isfetchUserLoading) return;
      this.isfetchUserLoading = true;
      if (this.user && !force) {
        this.isfetchUserLoading = false;
        return;
      }
      const { data, status } = await get('api/user/user-info/');
      if (status === 200) {
        this.user = data;
        // Associate Sentry events with the internal user id only (no PII).
        setSentryUser(data);
        // Same for analytics, keyed on the payload's `uuid`. Re-derived on
        // every fetch so a subscription or trial change mid-session stops
        // tagging events with stale status.
        identifyAnalyticsUser(data);
      }
      this.isfetchUserLoading = false;
      return { data, status };
    },

    async changePassword(current_password, new_password, confirm_password) {
      const { data, status } = await post(`api/user/update-password/`, {
        current_password,
        new_password,
        confirm_password,
      });
      return { data, status };
    },

    logoutUser(callback) {
      localStorage.clear();
      this.user = null;
      this.accessToken = '';
      this.refreshToken = '';
      clearSentryUser();
      resetAnalyticsIdentity();
      // Paywall exposure is memoised per session; without clearing it the
      // next user on this browser would look like they never hit a paywall.
      resetPaywallExposure();
      message.success('Logged out successfully!');
      callback && callback();
    },
    applyHandleUpdate({ handle, handle_is_auto_generated }) {
      if (!this.user) return;
      if (handle !== undefined) this.user.handle = handle;
      if (handle_is_auto_generated !== undefined)
        this.user.handle_is_auto_generated = handle_is_auto_generated;
    },
  },
});
